import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar, Clock, User } from 'lucide-react';

const Blog = ({ posts }) => {
  if (!posts || posts.length === 0) {
    return null;
  }

  return (
    <section id="blog" className="py-24 md:py-32 bg-[#121212]" data-testid="blog-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-[#d4af37] text-sm tracking-[0.3em] uppercase font-light mb-4">
            Insights & Tips
          </p>
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl lg:text-6xl font-medium text-[#e5e5e5]">
            From Our <span className="italic">Blog</span>
          </h2>
        </motion.div>

        {/* Blog Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {posts.map((post, index) => (
            <motion.article
              key={post.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group cursor-pointer"
              data-testid={`blog-post-${post.id}`}
            >
              {/* Image */}
              <div className="relative aspect-[4/3] overflow-hidden mb-6">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a]/80 via-transparent to-transparent" />
                
                {/* Category Badge */}
                <div className="absolute top-4 left-4 bg-[#d4af37] px-3 py-1">
                  <span className="text-[#0a0a0a] text-xs font-medium tracking-wider uppercase">
                    {post.category}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="space-y-4">
                {/* Meta */}
                <div className="flex items-center gap-4 text-[#a3a3a3] text-xs">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    <span>{new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    <span>{post.read_time}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <User className="w-3 h-3" />
                    <span>{post.author}</span>
                  </div>
                </div>

                {/* Title */}
                <h3 className="font-['Playfair_Display'] text-xl md:text-2xl text-[#e5e5e5] group-hover:text-[#d4af37] transition-colors line-clamp-2">
                  {post.title}
                </h3>

                {/* Excerpt */}
                <p className="text-[#a3a3a3] leading-relaxed line-clamp-2">
                  {post.excerpt}
                </p>

                {/* Read More */}
                <button className="flex items-center gap-2 text-[#d4af37] text-sm tracking-wider uppercase group/btn" data-testid={`blog-read-more-${post.id}`}>
                  Read More
                  <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-2 transition-transform" />
                </button>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Blog;
